/**
 * Estilo de marca para los gráficos (MUI X Charts y Nivo). Solo aspecto.
 *
 * Los valores son los de css/tokens.css escritos en hex: MUI y d3 aclaran/oscurecen los colores
 * de las series y no aceptan var(). Los colores con significado (tecnologías, MT/BT, impacto…)
 * no salen de acá: los define cada gráfico.
 */

export const VDR_CHART = {
  ink: '#0b0e10',
  ink3: '#18303a',
  ink4: '#1f4352',
  cyan: '#00b0d9',
  cyanDark: '#00708d',
  cyanDeep: '#00657f',
  text: '#22313a',
  text2: '#33424a',
  mute: '#5e6e75',
  mute2: '#7f9199',
  line: '#dce6ea',
  lineStrong: '#b9c8ce',
  soft: '#f1f6f8',
  soft2: '#e8f0f3',
  onDark: '#c7d3d8',
  darkLine: '#2a3a41',
  white: '#ffffff'
};

export const VDR_FONT_BODY = "'Source Sans 3', system-ui, -apple-system, 'Segoe UI', sans-serif";
export const VDR_FONT_MONO = "'JetBrains Mono', ui-monospace, 'SFMono-Regular', Consolas, monospace";

/**
 * Paleta categórica de marca para series SIN significado propio, en orden fijo (no se cicla
 * por rango). Cada serie difiere de las demás en luminosidad (OKLCH L ≈ 0,16 · 0,70 · 0,53 ·
 * 0,36), no solo en tono. Validada en todos los pares (OKLab ×100): visión normal ≥ 16,7 y
 * protan/deutan ≥ 15,7. Las series 4 y 5 además van con trazo discontinuo (vdrSeriesDashSx).
 */
export const VDR_SERIES_COLORS = [
  VDR_CHART.ink,
  VDR_CHART.cyan,
  VDR_CHART.mute,
  VDR_CHART.ink4,
  VDR_CHART.mute2
];

/** Codificación secundaria para las series 4 y 5 cuando usan la paleta por defecto. */
export const vdrSeriesDashSx = {
  '& .MuiLineElement-series-auto-generated-id-3': { strokeDasharray: '7 4' },
  '& .MuiLineElement-series-auto-generated-id-4': { strokeDasharray: '2 3' }
};

/** Superficie del gráfico (SVG de MUI X): ejes, grilla y textos con la tipografía de marca. */
export const vdrChartSx = {
  '& .MuiChartsAxis-root .MuiChartsAxis-line': {
    stroke: VDR_CHART.lineStrong
  },
  '& .MuiChartsAxis-root .MuiChartsAxis-tick': {
    stroke: VDR_CHART.lineStrong
  },
  '& .MuiChartsAxis-root .MuiChartsAxis-tickLabel': {
    fill: VDR_CHART.mute,
    fontFamily: VDR_FONT_BODY,
    fontWeight: 400,
    fontVariantNumeric: 'tabular-nums'
  },
  '& .MuiChartsAxis-root .MuiChartsAxis-label': {
    fill: VDR_CHART.text2,
    fontFamily: VDR_FONT_BODY,
    fontWeight: 600
  },
  '& .MuiChartsGrid-root .MuiChartsGrid-line': {
    stroke: VDR_CHART.line
  },
  '& .MuiChartsGrid-root .MuiChartsGrid-verticalLine': {
    stroke: VDR_CHART.soft2
  },
  '& .MuiChartsAxisHighlight-root': {
    stroke: VDR_CHART.mute2,
    strokeDasharray: 'none'
  }
};

/** Tooltip de MUI X (se monta en un portal, por eso va por `slotProps.popper.sx`). */
export const vdrTooltipPopperSx = {
  '& .MuiChartsTooltip-paper': {
    backgroundColor: VDR_CHART.ink,
    color: VDR_CHART.white,
    border: `1px solid ${VDR_CHART.ink3}`,
    borderRadius: 0,
    boxShadow: '0 12px 28px rgba(11, 14, 16, 0.22)'
  },
  // Neutraliza la regla global `td { border; padding }` de PrivateStudyResult.css.
  '& .MuiChartsTooltip-table td': {
    border: 'none',
    padding: '4px 10px'
  },
  '& .MuiChartsTooltip-table td:first-of-type': {
    paddingLeft: '14px'
  },
  '& .MuiChartsTooltip-table td:last-of-type': {
    paddingRight: '14px'
  },
  '& .MuiChartsTooltip-table tbody tr:first-of-type td': {
    paddingTop: '9px'
  },
  '& .MuiChartsTooltip-table tbody tr:last-of-type td': {
    paddingBottom: '10px'
  },
  '& .MuiChartsTooltip-table thead td': {
    padding: '9px 14px 8px',
    borderBottom: `1px solid ${VDR_CHART.darkLine}`
  },
  '& .MuiChartsTooltip-table thead .MuiTypography-root': {
    fontFamily: VDR_FONT_MONO,
    fontSize: 11.5,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: VDR_CHART.cyan
  },
  '& .MuiChartsTooltip-table .MuiChartsTooltip-cell': {
    color: VDR_CHART.onDark
  },
  '& .MuiChartsTooltip-table .MuiChartsTooltip-cell .MuiTypography-root': {
    fontFamily: VDR_FONT_BODY,
    fontSize: 13.5
  },
  '& .MuiChartsTooltip-table .MuiChartsTooltip-valueCell .MuiTypography-root': {
    fontFamily: VDR_FONT_MONO,
    fontSize: 12.5,
    fontWeight: 500,
    color: VDR_CHART.white,
    fontVariantNumeric: 'tabular-nums'
  },
  // Filete claro para que las muestras oscuras (tinta, ink-4) se vean sobre el fondo tinta.
  '& .MuiChartsTooltip-table .MuiChartsTooltip-mark': {
    borderRadius: 0,
    boxShadow: 'none',
    border: `1px solid ${VDR_CHART.mute2}`
  }
};

/** Mensajes de MUI X traducidos (por defecto vienen en inglés: "No data to display"…). */
export const vdrOverlaySlotProps = {
  noDataOverlay: { message: 'Sin datos para mostrar' },
  loadingOverlay: { message: 'Cargando datos…' }
};

/** Números de los ejes en formato chileno (1.000; 0,5), como las cifras del resto de la página. */
export const formatearTickEsCl = (v: number | null): string =>
  typeof v === 'number' && Number.isFinite(v) ? v.toLocaleString('es-CL') : '';

/** Texto de leyenda (SVG): MUI lo mide con este mismo estilo para ubicar los ítems. */
export const vdrLegendLabelStyle = {
  fontFamily: VDR_FONT_BODY,
  fontSize: 12.5,
  fontWeight: 600,
  fill: VDR_CHART.text2
};

/** Tinta o blanco, lo que contraste más sobre un relleno de color (etiquetas dentro de celdas). */
export const colorTextoSobre = (fondo: string): string => {
  const hex = fondo.replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return VDR_CHART.ink;
  const canal = (i: number) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const lum = 0.2126 * canal(0) + 0.7152 * canal(2) + 0.0722 * canal(4);
  const contrasteBlanco = 1.05 / (lum + 0.05);
  const contrasteTinta = (lum + 0.05) / (0.0045 + 0.05);
  return contrasteTinta >= contrasteBlanco ? VDR_CHART.ink : VDR_CHART.white;
};
